import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Home, Wallet, MessageCircle, User, FileText, Shield, 
  Bell, Search, Bookmark, LogOut, Settings, Lock, 
  AlertCircle, Briefcase, FileCheck, Menu, X, ChevronRight, 
  Globe, User2, 
  Settings2, ChartAreaIcon, Star, UserCheck, Users, History
} from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/badge";
import { Avatar } from "../components/ui/avatar";
import PolicyModals from "../components/ui/PolicyModals";
import SharedHeader5 from "../Headers/SharedHeader5";
import SharedFooter3 from "../Footer/SharedFooter3";
import { MdReviews } from "react-icons/md";
import { useAppDispatch, getFavoriteByUserId } from "../redux";
import FavoriteButton from "../components/ui/FavoriteButton";
import ReviewModal from "../components/ui/ReviewModal";
import SocialMediaLinks from "../components/ui/SocialMediaLinks";

const Favorites22 = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showProfessionalTermsModal, setShowProfessionalTermsModal] = useState(false);

  // Redux state
  const { user, isAuthenticated } = useSelector((state: any) => state.auth);
  const { currentUserFavorite, loading } = useSelector((state: any) => state.favorites);
  const dispatch = useAppDispatch();
  
  // Local state for favorite professionals data
  const [favoriteProfessionalsData, setFavoriteProfessionalsData] = useState([]);
  const [professionalsLoading, setProfessionalsLoading] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedProfessionalForReview, setSelectedProfessionalForReview] = useState(null);
  const hasInitialized = useRef(false);
  const lastFetchedUserIds = useRef<string[]>([]);

  const processUserData = useCallback((userIds: any[]) => {
    if (!userIds || userIds.length === 0) return [];

    setProfessionalsLoading(true);
    try {
      return userIds.map(user => ({
        id: user.id,
        name: `${user.firstName} ${user.lastName}`,
        avatar: user.photo?.path,
        profession: user.title || t("favorites.professional"),
        rating: user.averageRating || 0,
        totalJobs: user.completedJobs || 0,
        location: user.address || t("favorites.noLocation"),
        isOnline: true, // Mock online status
        tags: user.skills || [t("favorites.generalServices")],
        description: user.bio || t("favorites.professionalServiceProvider"),
        hourlyRate: user.hourlyRate || "$50",
        languages: user.languages || ["English"],
        verified: user.verified || false,
        backgroundChecked: user.backgroundChecked || false,
        reviewsCount: user.reviewsCount || 0,
        reviewsList: user.reviewsList || []
      }));
    } catch (error) {
      return [];
    } finally {
      setProfessionalsLoading(false);
    }
  }, []);

  // Load favorites when component mounts
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      dispatch(getFavoriteByUserId(user.id));
    }
  }, [ user?.id]);

  // Process user data when favorites are loaded
  useEffect(() => {
    if (isAuthenticated && currentUserFavorite?.userIds) {
      const userIds = currentUserFavorite.userIds;
      
      // Only process if userIds have actually changed
      const userIdsString = userIds.map(u => u.id).sort().join(",");
      const lastFetchedString = lastFetchedUserIds.current.sort().join(",");
      
      if (userIdsString !== lastFetchedString) {
        lastFetchedUserIds.current = userIds.map(u => u.id);
        const userData = processUserData(userIds);
        setFavoriteProfessionalsData(userData);
      }
    } else if (!isAuthenticated) {
      // For unauthenticated users, we can't get full user data, so show empty
      setFavoriteProfessionalsData([]);
    } else if (isAuthenticated && !currentUserFavorite?.userIds) {
      setFavoriteProfessionalsData([]);
    }
  }, [isAuthenticated, currentUserFavorite?.userIds, processUserData]);

  // StarRatingDisplay component for showing user ratings
  const StarRatingDisplay = ({ rating, count, size = "sm" }: { rating: number; count: number; size?: "sm" | "md" | "lg" }) => {
    const sizeClasses = {
      sm: "h-4 w-4",
      md: "h-5 w-5", 
      lg: "h-6 w-6"
    };

    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="flex items-center gap-1">
        <div className="flex">
          {[...Array(fullStars)].map((_, i) => (
            <Star key={i} className={`${sizeClasses[size]} text-yellow-400 fill-current`} />
          ))}
          {hasHalfStar && (
            <div className="relative">
              <Star className={`${sizeClasses[size]} text-gray-300`} />
              <div className="absolute inset-0 overflow-hidden" style={{ width: "50%" }}>
                <Star className={`${sizeClasses[size]} text-yellow-400 fill-current`} />
              </div>
            </div>
          )}
          {[...Array(emptyStars)].map((_, i) => (
            <Star key={i + fullStars + (hasHalfStar ? 1 : 0)} className={`${sizeClasses[size]} text-gray-300`} />
          ))}
        </div>
        <span className="text-sm font-medium text-blue-700">
          {rating.toFixed(1)} ({count} {t("favorites.reviews")})
        </span>
      </div>
    );
  };


  const StoreLinks = () => (
    <div className="flex justify-center gap-2 pt-6 px-4">
      <a
        href="https://play.google.com/store/apps/details?id=com.kokobeo"
        className="w-1/2"
      >
        <img
          src="https://texttofloss.com/wp-content/uploads/2021/01/Google-Play-Store-Button.png"
          alt={t("favorites.getItOnGooglePlay")}
          className="w-full h-auto rounded-md hover:opacity-90 transition-opacity"
          style={{ maxWidth: "120px" }}
        />
      </a>
      <a href="https://apps.apple.com/app/kokobeo" className="w-1/2">
        <img
          src="https://e7.pngegg.com/pngimages/422/842/png-clipart-apple-store-logo-app-store-android-google-play-get-started-now-button-text-logo.png"
          alt={t("favorites.downloadOnAppStore")}
          className="w-full h-auto rounded-md hover:opacity-90 transition-opacity"
          style={{ maxWidth: "120px" }}
        />
      </a>
    </div>
  );
  
  const SocialIcons = () => (
    <SocialMediaLinks className="flex justify-center space-x-3 pt-4 px-4 pb-6 border-t border-gray-100 mt-2" iconSize="h-4 w-4" />
  );

  return (
    <div className="min-h-screen bg-gray-50" style={{ textAlign: "left" }}>
      {/* Fixed Header */}
      <div className="fixed top-0 inset-x-0 z-50 bg-white border-b h-16 w-full">
        <SharedHeader5 />
      </div>

      <div className="flex min-h-[calc(100vh-64px)] pt-16">

        {/* Main Content */}
        <div className="flex-1 w-full">
          <main className="p-4 lg:p-8 overflow-y-auto h-[calc(100vh-64px)] w-full">
            <div className="max-w-7xl mx-auto space-y-8 w-full">
              {/* Page Header */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 w-full">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{t("favorites.professionalsTitle")}</h1>
                  <p className="text-gray-600 mt-1">
                    {t("favorites.professionalsSubtitle")}
                  </p>
                </div>
                <div className="flex items-center gap-2 lg:hidden">
                  {/* <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => setIsMobileMenuOpen(true)}
                  >
                    <Menu className="h-5 w-5 mr-2" />
                    {t("favorites.menu")}
                  </Button> */}
                </div>
              </div>

              {/* Professionals Card */}
              <Card className="overflow-hidden w-full">
                <div className="flex items-center justify-between p-4 border-b">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <UserCheck className="h-5 w-5 mr-2 text-blue-600" />
                    {t("favorites.yourFavoriteProfessionals")}
                  </h2>
                  <Button variant="outline" size="sm">
                    <Star className="h-4 w-4 mr-2" />
                    {t("favorites.manageFavorites")}
                  </Button>
                </div>

                <div className="p-4 bg-blue-50 flex items-center gap-3">
                  <Search className="h-5 w-5 text-blue-600" />
                  <p className="text-sm text-blue-700">
                    {t("favorites.professionalsDesc")}
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 p-4">
                  {professionalsLoading ? (
                    <div className="col-span-full flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : favoriteProfessionalsData.length > 0 ? (
                    favoriteProfessionalsData.map((professional) => (
                      <Card key={professional.id} className="overflow-hidden hover:shadow-md transition-shadow">
                        <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              {professional.avatar ? (
                                <img 
                                  src={professional.avatar} 
                                  alt={professional.name} 
                                  className="h-12 w-12 rounded-full object-cover border-2 border-white"
                                />
                              ) : (
                                <div className="h-12 w-12 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center">
                                  <User className="h-6 w-6 text-gray-500" />
                                </div>
                              )}
                              {professional.isOnline && (
                                <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-green-500 border-2 border-white"></span>
                              )}
                            </div>
                            <div>
                              <h3 className="font-medium text-gray-900">{professional.name}</h3>
                              <p className="text-sm text-gray-600">{professional.profession}</p>
                            </div>
                          </div>
                          <FavoriteButton
                            targetUserId={professional.id}
                            size="sm"
                          />
                        </div>
                        <div className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            {professional.reviewsCount > 0 ? (
                              <button
                                onClick={() => {
                                  setSelectedProfessionalForReview(professional);
                                  setShowReviewModal(true);
                                }}
                                className="flex items-center gap-1 hover:opacity-80 transition-opacity cursor-pointer"
                                title={t("favorites.clickToViewAllReviews")}
                              >
                                <StarRatingDisplay 
                                  rating={professional.rating} 
                                  count={professional.reviewsCount} 
                                  size="sm" 
                                />
                              </button>
                            ) : (
                              <div className="flex items-center gap-1">
                                <Star className="h-4 w-4 text-gray-300" />
                                <span className="text-sm text-gray-500">{t("favorites.noReviews")}</span>
                              </div>
                            )}
                            <span className="text-gray-300">|</span>
                            <span className="text-sm text-gray-600">{professional.totalJobs} {t("favorites.jobs")}</span>
                          </div>
                          <p className="text-sm text-gray-700 mb-3 flex items-center">
                            <Globe className="h-4 w-4 mr-1 text-gray-500" />
                            {professional.location}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-3">
                            {professional.tags.map((tag, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div className="p-4 border-t bg-gray-50 flex justify-between">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => navigate(`/myprofile/${professional.id}`)}
                          >
                            {t("favorites.viewProfile")}
                          </Button>
                          <Button size="sm" onClick={()=>{
                            navigate("/client/MessageAndVideoCall?selectedUserId=" + professional.id);
                          }}>{t("favorites.contact")}</Button>
                        </div>
                      </Card>
                    ))
                  ) : (
                    <div className="col-span-full p-8 text-center">
                      <div className="mx-auto w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                        <UserCheck className="h-8 w-8 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">{t("favorites.noFavoritesYet")}</h3>
                      <p className="text-gray-600 mb-4">{t("favorites.noFavoritesDesc")}</p>
                      <div className="flex gap-2 justify-center">
                        <Button onClick={() => navigate("/local-professionals")}>
                          {t("favorites.browseLocal")}
                        </Button>
                        <Button variant="outline" onClick={() => navigate("/professional-international")}>
                          {t("favorites.browseInternational")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </main>

          {/* Footer */}
          <footer className="bg-white border-t mt-auto w-full">
            <div className="max-w-7xl mx-auto">
              <SharedFooter3 />
            </div>
          </footer>
        </div>
      </div>

      {/* Modals */}
      <PolicyModals
        showPrivacyModal={showPrivacyModal}
        showTermsModal={showTermsModal}
        showProfessionalTermsModal={showProfessionalTermsModal}
        onClosePrivacy={() => setShowPrivacyModal(false)}
        onCloseTerms={() => setShowTermsModal(false)}
        onCloseProfessionalTerms={() => setShowProfessionalTermsModal(false)}
      />

      {/* Review Modal */}
      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        professional={selectedProfessionalForReview ? {
          id: selectedProfessionalForReview.id,
          name: selectedProfessionalForReview.name,
          firstName: selectedProfessionalForReview.name.split(" ")[0],
          lastName: selectedProfessionalForReview.name.split(" ").slice(1).join(" "),
          title: selectedProfessionalForReview.profession,
          photo: selectedProfessionalForReview.avatar ? { path: selectedProfessionalForReview.avatar } : undefined,
          averageRating: selectedProfessionalForReview.rating,
          reviewsCount: selectedProfessionalForReview.reviewsCount,
          reviewsList: selectedProfessionalForReview.reviewsList
        } : null}
      />
    </div>
  );
};

export default Favorites22;